import dagre from '@dagrejs/dagre';
import type { AppEdge, AppNode } from '../types';
import { NODE_DEFAULTS } from '../store/flowStore';
import type { NodeKind } from '../types';

export type LayoutDirection = 'TB' | 'LR';

function nodeSize(node: AppNode): { width: number; height: number } {
  const defaults = NODE_DEFAULTS[(node.type ?? 'process') as NodeKind];
  return {
    width: node.measured?.width ?? node.width ?? defaults?.width ?? 180,
    height: node.measured?.height ?? node.height ?? defaults?.height ?? 70,
  };
}

/** Sobe a cadeia de pais até encontrar o ancestral de nível superior. */
function topLevelAncestor(id: string, byId: Map<string, AppNode>): string {
  let current = byId.get(id);
  while (current?.parentId && byId.has(current.parentId)) {
    current = byId.get(current.parentId);
  }
  return current?.id ?? id;
}

/**
 * Layout automático com dagre. Apenas nós de nível superior são
 * reposicionados; filhos de grupos mantêm suas posições relativas
 * (o grupo inteiro se move como um bloco).
 */
export function layoutGraph(
  nodes: AppNode[],
  edges: AppEdge[],
  direction: LayoutDirection,
): AppNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const topLevel = nodes.filter((n) => !n.parentId);
  if (topLevel.length === 0) return nodes;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: direction, nodesep: 70, ranksep: 90, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const node of topLevel) {
    g.setNode(node.id, nodeSize(node));
  }

  const seen = new Set<string>();
  for (const edge of edges) {
    const source = topLevelAncestor(edge.source, byId);
    const target = topLevelAncestor(edge.target, byId);
    if (source === target) continue;
    const key = `${source}->${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (g.hasNode(source) && g.hasNode(target)) g.setEdge(source, target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    if (node.parentId) return node;
    const placed = g.node(node.id);
    if (!placed) return node;
    const { width, height } = nodeSize(node);
    return {
      ...node,
      position: { x: placed.x - width / 2, y: placed.y - height / 2 },
    };
  });
}
